import React from 'react';
import { Button } from '../ui/index.js';

interface LandingHeroProps {
  onGuestClick: () => void;
}

const DESKTOP_NOTES = [
  { left: '3.6%', top: '0px', val: '12' },
  { left: '7.6%', top: '13.8px', val: '15' },
  { left: '11.6%', top: '27.6px', val: '12' },
  { left: '15.6%', top: '13.8px', val: '12' },
  { left: '19.6%', top: '0px', val: '14' },
  { left: '23.6%', top: '13.8px', val: '14' },
  { left: '27.6%', top: '27.6px', val: '14' },
  { left: '31.6%', top: '41.4px', val: '14' },
  { left: '35.6%', top: '27.6px', val: '15' },
  { left: '39.6%', top: '13.8px', val: '15', active: true },
  { left: '43.6%', top: '0px', val: '11' },
  { left: '47.6%', top: '13.8px', val: '15' },
  { left: '51.6%', top: '41.4px', val: '12' },
  { left: '55.6%', top: '55.2px', val: '14' },
  { left: '59.6%', top: '41.4px', val: '17' },
  { left: '63.6%', top: '27.6px', val: '12' },
  { left: '67.6%', top: '0px', val: '12' },
  { left: '71.6%', top: '13.8px', val: '15' },
  { left: '75.6%', top: '27.6px', val: '12' },
  { left: '79.6%', top: '13.8px', val: '12' },
  { left: '83.6%', top: '0px', val: '14' },
  { left: '87.6%', top: '13.8px', val: '14' },
  { left: '91.6%', top: '27.6px', val: '14' },
  { left: '95.6%', top: '41.4px', val: '14' }
];

const MOBILE_NOTES = [
  { left: '4%', top: '0px', val: '12' },
  { left: '12%', top: '11.8px', val: '15' },
  { left: '20%', top: '23.6px', val: '14' },
  { left: '28%', top: '11.8px', val: '15' },
  { left: '36%', top: '0px', val: '12' },
  { left: '44%', top: '11.8px', val: '14' },
  { left: '52%', top: '23.6px', val: '12' },
  { left: '60%', top: '35.4px', val: '14' },
  { left: '68%', top: '23.6px', val: '15' },
  { left: '76%', top: '11.8px', val: '12' },
  { left: '84%', top: '0px', val: '17' },
  { left: '92%', top: '11.8px', val: '15' }
];

export const LandingHero: React.FC<LandingHeroProps> = ({ onGuestClick }) => {
  return (
    <section
      data-testid="landing-hero"
      className="max-w-[1248px] mx-auto px-5 md:px-6 pt-12 md:pt-24 pb-0 md:pb-16 flex flex-col items-start text-start md:items-center md:text-center gap-[18px] md:gap-6"
    >
      {/* Status Pill */}
      <span
        data-testid="landing-status-pill"
        className="h-8 px-3.5 border border-base-300 rounded-full bg-base-200 text-xs md:text-[13px] text-base-content/80 inline-flex items-center gap-2 select-none"
      >
        <span className="size-1.5 rounded-full bg-secondary shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">رایگان · کاملاً آفلاین · وب و اندروید</span>
        <span className="sm:hidden">رایگان · کاملاً آفلاین</span>
      </span>

      {/* H1 Display */}
      <h1 className="max-w-[820px] text-[38px] md:text-6xl font-black leading-[1.3] md:leading-[1.25] tracking-tight text-base-content m-0">
        تمرین گیتار، دقیق و بی‌وقفه
      </h1>

      {/* Subtitle paragraph */}
      <p className="max-w-[640px] text-base md:text-lg leading-[1.9] md:leading-[1.9] text-base-content/60 m-0">
        <span className="sm:hidden">
          فایل‌های Guitar Pro را با سرعت دلخواه بنوازید، روتین بسازید، کوک کنید و پیشرفت تمپوی تمیزتان را ثبت کنید؛ حتی بدون اینترنت.
        </span>
        <span className="hidden sm:inline">
          فایل‌های Guitar Pro را با سرعت دلخواه بنوازید، روتین زمان‌بندی‌شده بسازید، با تیونر دقیق کوک کنید و پیشرفت تمپوی تمیزتان را ثبت کنید؛ حتی بدون اینترنت.
        </span>
      </p>

      {/* Dual CTAs */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3 w-full sm:w-auto mt-1 sm:mt-2">
        <Button
          variant="primary"
          size="lg"
          data-testid="landing-cta-register"
          href="/auth?mode=register"
          fullWidth
          className="sm:w-auto h-[52px] px-6 rounded-[14px] text-base font-bold justify-center"
        >
          ساخت حساب رایگان
        </Button>
        <Button
          variant="secondary"
          size="lg"
          data-testid="landing-cta-guest"
          onClick={onGuestClick}
          fullWidth
          className="sm:w-auto h-[52px] px-6 rounded-[14px] text-base font-semibold justify-center"
        >
          امتحان بدون ثبت‌نام
        </Button>
      </div>

      {/* Realistic Studio Notation Mockup */}
      <div
        data-testid="landing-notation-mockup"
        aria-hidden="true"
        className="w-full max-w-[1080px] mt-3.5 md:mt-10 bg-base-200 border border-base-300 rounded-[20px] shadow-2xl overflow-hidden text-right select-none"
      >
        {/* Mockup Header */}
        <div className="flex flex-col items-start gap-0.5 py-3.5 px-4 sm:flex-row sm:items-center sm:gap-3 sm:h-12 sm:py-0 md:px-4.5 border-b border-base-300">
          <span className="text-sm font-bold text-base-content" dir="ltr">
            Tornado of Souls
          </span>
          <span className="text-xs text-base-content/60 hidden sm:inline" dir="ltr">
            Megadeth · E Std
          </span>
          <span className="text-[11px] font-mono text-base-content/60 sm:hidden" dir="ltr">
            Lead Guitar · E Std
          </span>
          <div className="flex-1" />
          <span className="hidden sm:inline-flex h-[26px] px-2.5 rounded-lg bg-primary/14 text-primary-soft text-xs font-semibold items-center">
            میکسر
          </span>
        </div>

        {/* Mockup Staff Body */}
        {/* Desktop Layout (md and up) */}
        <div dir="ltr" className="hidden md:flex">
          <div className="flex-1 p-9 px-10 flex flex-col gap-7.5">
            <div className="relative h-[70px]">
              {/* Emerald Loop Region */}
              <div className="absolute left-[25%] w-[50%] -top-1.5 -bottom-1.5 bg-secondary/8 border-t-2 border-secondary pointer-events-none" />

              {/* 6 Staff Horizontal Lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                <div className="h-px bg-base-content/25" />
                <div className="h-px bg-base-content/25" />
                <div className="h-px bg-base-content/25" />
                <div className="h-px bg-base-content/25" />
                <div className="h-px bg-base-content/25" />
                <div className="h-px bg-base-content/25" />
              </div>

              {/* Measure Bars */}
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-base-content/40" />
              <div className="absolute left-[25%] top-0 bottom-0 w-px bg-base-content/40" />
              <div className="absolute left-[50%] top-0 bottom-0 w-px bg-base-content/40" />
              <div className="absolute left-[75%] top-0 bottom-0 w-px bg-base-content/40" />
              <div className="absolute right-0 top-0 bottom-0 w-px bg-base-content/40" />

              {/* Tab Fret Numbers */}
              {DESKTOP_NOTES.map((note, index) => (
                <span
                  key={index}
                  style={{ left: note.left, top: note.top }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 px-0.5 bg-base-200 font-mono text-xs font-semibold leading-none ${
                    note.active ? 'text-primary' : 'text-base-content'
                  }`}
                >
                  {note.val}
                </span>
              ))}

              {/* Primary Playback Cursor */}
              <div className="absolute left-[39.6%] -top-2.5 -bottom-2.5 w-[3px] rounded-sm bg-primary shadow-lg shadow-primary/50" />
            </div>
          </div>

          {/* Docked Mixer Panel */}
          <div className="flex-[0_0_240px] border-l border-base-300 p-5 flex flex-col gap-2.5 font-mono text-xs">
            <div className="border border-primary rounded-xl p-2.5 flex justify-between bg-primary/5">
              <span>Lead Guitar</span>
              <span className="text-base-content/60">100%</span>
            </div>
            <div className="border border-base-300 rounded-xl p-2.5 flex justify-between">
              <span>Rhythm</span>
              <span className="text-base-content/60">70%</span>
            </div>
            <div className="border border-base-300 rounded-xl p-2.5 flex justify-between text-base-content/70">
              <span>Drums</span>
              <span>M</span>
            </div>
          </div>
        </div>

        {/* Mobile Staff Layout (< md) */}
        <div dir="ltr" className="md:hidden py-7 px-4.5">
          <div className="relative h-[60px]">
            {/* Emerald Loop Region */}
            <div className="absolute left-[30%] w-[45%] -top-1.5 -bottom-1.5 bg-secondary/8 border-t-2 border-secondary pointer-events-none" />

            {/* 6 Staff Horizontal Lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              <div className="h-px bg-base-content/25" />
              <div className="h-px bg-base-content/25" />
              <div className="h-px bg-base-content/25" />
              <div className="h-px bg-base-content/25" />
              <div className="h-px bg-base-content/25" />
              <div className="h-px bg-base-content/25" />
            </div>

            {/* Tab Fret Numbers */}
            {MOBILE_NOTES.map((note, index) => (
              <span
                key={index}
                style={{ left: note.left, top: note.top }}
                className="absolute -translate-x-1/2 -translate-y-1/2 px-0.5 bg-base-200 font-mono text-[11px] font-semibold leading-none text-base-content"
              >
                {note.val}
              </span>
            ))}

            {/* Primary Playback Cursor */}
            <div className="absolute left-[46%] -top-2 -bottom-2 w-[3px] rounded-sm bg-primary shadow-lg shadow-primary/50" />
          </div>
        </div>

        {/* Transport Bar */}
        <div
          dir="ltr"
          className="h-12 md:h-15 flex items-center gap-2.5 md:gap-3.5 px-4 md:px-4.5 border-t border-base-300 font-mono text-[11px] md:text-xs"
        >
          <span className="size-8 md:size-9 rounded-full bg-primary-fill text-white flex items-center justify-center shrink-0">
            <svg className="size-3 md:size-3.5 fill-current" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </span>
          <span className="hidden sm:inline text-base-content">1:42 / 5:22</span>
          <span className="h-6 md:h-7 px-2 md:px-2.5 rounded-lg bg-secondary/12 text-secondary-soft font-medium inline-flex items-center text-xs shrink-0">
            <span className="hidden sm:inline">Loop 34–38</span>
            <span className="sm:hidden">Loop</span>
          </span>
          <div className="flex-1 h-1.5 rounded-full bg-base-300 overflow-hidden">
            <div className="w-[72%] h-full rounded-full bg-primary-fill" />
          </div>
          <span className="font-bold text-base-content shrink-0">75%</span>
        </div>
      </div>
    </section>
  );
};
